/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './Pulse';

const PulseManager: ComponentManagerFn<'Pulse'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Pulse'>(platform, 'Pulse'),
    type: 'Pulse',
    name: 'Pulse',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        category: 'Data',
        allowedTypes: [
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
          'TransientDataStore',
        ],
      },
      idAttribute: {
        label: 'ID Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      nameAttribute: {
        label: 'Name Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      xAttribute: {
        label: 'X Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      yAttribute: {
        label: 'Y Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Integer', 'Double'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'idAttribute':
        case 'nameAttribute':
        case 'xAttribute':
        case 'yAttribute':
          return !!itemData.alias;
        default:
          return true;
      }
    },
    replaceableWith: ['Device', 'Area', 'Tracker'],
    getDisplayText: (item) => item.props.nameAttribute,
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The Pulse component is a visualization tool for the **IoT Image Mapper** designed to highlight critical points of interest with an animated "pulsing" effect. Unlike the static icons of the Device component, Pulse draws attention to specific coordinates using a radiating circle animation that visually "pings" the user.
Use this component to signal alerts, active alarms, or live tracking updates where grabbing the user's immediate attention is a priority (e.g., "Active Fire Alarm" or "Security Breach").

**1. Data Connection**<br/>
Like other mapping layers, Pulse connects to a Data Store to determine where and what to display.
* **Alias:** The Data Store containing the records to visualize.
* **ID Attribute:** A unique identifier for the pulse marker (e.g., "Alert-ID-99").
* **Name Attribute:** A label or name associated with the pulse, often used for tooltips or descriptive labels (e.g., "High Temp Alert").

**2. Positioning**<br/>
The component requires precise coordinate data to locate the pulse center on the map.
* **X Attribute:** The horizontal position field (Integer or Double).
* **Y Attribute:** The vertical position field (Integer or Double).
* **Accuracy:** These coordinates map directly to the background image of the **IoT Image Mapper**, ensuring the pulse remains pinned to the physical location of the event.

**3. Visual Behavior**<br/>
* **Animation:** The primary feature is the visual "pulse"—typically a solid dot surrounded by expanding, fading rings. This animation loops continuously to create a sense of urgency or real-time activity.
* **Differentiation:** While the **Device** component is better for identifying *what* is at a location (using icons), **Pulse** is superior for showing *status* or *events* (indicating that something is currently happening at that location).

**4. Interactivity**<br/>
* **TriggerOnClick:** Supports the **TriggerOnClick** child component.
* **Action:** Clicking the pulsing dot can trigger critical workflows, such as acknowledging an alarm, opening a detail view of a sensor's telemetry, or navigating to a specific incident report.

**5. Best Use Cases**<br/>
* **Emergency Management:** Flashing red pulses at locations where smoke detectors or emergency pull stations have been triggered.
* **Real-Time Tracking:** Showing the current "ping" location of a moving asset or vehicle on a yard map.
* **Status Indicators:** Highlighting machines that are currently "Offline" or in a "Critical" error state on a factory floor schematic.
* **User Guidance:** Guiding a user to a specific location on a complex map (e.g., "You are here" markers or highlighting a specific search result).`,
  };
};

export default PulseManager;
